/**
 * Tooltip Component
 * Gère les interactions pour les tooltips d'aide contextuelle
 */

class TooltipManager {
    constructor() {
        this.tooltips = [];
        this.activeTooltip = null;
        this.init();
    }

    init() {
        // Initialiser tous les tooltips présents sur la page
        this.initTooltips();

        // Écouter les clics globaux pour fermer les tooltips
        document.addEventListener("click", this.handleGlobalClick.bind(this));

        // Écouter les touches clavier pour l'accessibilité
        document.addEventListener("keydown", this.handleKeydown.bind(this));

        // Observer les nouveaux tooltips ajoutés dynamiquement (Livewire)
        this.observeNewTooltips();
    }

    initTooltips() {
        const tooltipElements = document.querySelectorAll("[data-tooltip]");

        tooltipElements.forEach((tooltip) => {
            if (!tooltip.dataset.tooltipInitialized) {
                this.initTooltip(tooltip);
                tooltip.dataset.tooltipInitialized = "true";
            }
        });
    }

    initTooltip(tooltipElement) {
        const trigger = tooltipElement.querySelector("[data-tooltip-trigger]");
        const content = tooltipElement.querySelector("[data-tooltip-content]");

        if (!trigger || !content) return;

        const tooltipInstance = {
            element: tooltipElement,
            trigger: trigger,
            content: content,
            isOpen: false,
        };

        // Événements pour desktop (hover)
        if (this.isDesktop()) {
            trigger.addEventListener("mouseenter", () =>
                this.showTooltip(tooltipInstance),
            );
            trigger.addEventListener("mouseleave", () =>
                this.hideTooltip(tooltipInstance),
            );

            // Garder le tooltip ouvert quand on survole le contenu
            content.addEventListener("mouseenter", () =>
                this.showTooltip(tooltipInstance),
            );
            content.addEventListener("mouseleave", () =>
                this.hideTooltip(tooltipInstance),
            );
        }

        // Événements pour mobile et accessibilité (click/tap)
        trigger.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.toggleTooltip(tooltipInstance);
        });

        // Événements clavier pour l'accessibilité
        trigger.addEventListener("keydown", (e) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                this.toggleTooltip(tooltipInstance);
            }
            if (e.key === "Escape") {
                this.hideTooltip(tooltipInstance);
            }
        });

        this.tooltips.push(tooltipInstance);
    }

    showTooltip(tooltipInstance) {
        // Fermer les autres tooltips
        this.hideAllTooltips();

        const { trigger, content } = tooltipInstance;

        // Positionner le tooltip avant de l'afficher
        this.positionTooltip(tooltipInstance);

        // Afficher le tooltip
        content.classList.add("tooltip__content--visible");
        content.setAttribute("aria-hidden", "false");
        trigger.setAttribute("aria-expanded", "true");

        tooltipInstance.isOpen = true;
        this.activeTooltip = tooltipInstance;
    }

    hideTooltip(tooltipInstance) {
        if (!tooltipInstance.isOpen) return;

        const { trigger, content } = tooltipInstance;

        content.classList.remove("tooltip__content--visible");
        content.setAttribute("aria-hidden", "true");
        trigger.setAttribute("aria-expanded", "false");

        tooltipInstance.isOpen = false;

        if (this.activeTooltip === tooltipInstance) {
            this.activeTooltip = null;
        }
    }

    toggleTooltip(tooltipInstance) {
        if (tooltipInstance.isOpen) {
            this.hideTooltip(tooltipInstance);
        } else {
            this.showTooltip(tooltipInstance);
        }
    }

    hideAllTooltips() {
        this.tooltips.forEach((tooltip) => {
            if (tooltip.isOpen) {
                this.hideTooltip(tooltip);
            }
        });
    }

    positionTooltip(tooltipInstance) {
        const { content, trigger } = tooltipInstance;
        const triggerRect = trigger.getBoundingClientRect();
        const contentRect = content.getBoundingClientRect();
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight,
        };

        // Réinitialiser les classes de position
        content.classList.remove(
            "tooltip__content--top",
            "tooltip__content--bottom",
            "tooltip__content--left",
            "tooltip__content--right",
        );

        // Déterminer la meilleure position
        let position = "top"; // Position par défaut

        // Vérifier si on a assez de place en haut
        if (triggerRect.top < contentRect.height + 20) {
            position = "bottom";
        }

        // Vérifier si on a assez de place en bas
        if (triggerRect.bottom + contentRect.height + 20 > viewport.height) {
            position = "top";
        }

        // Sur mobile, préférer le bas pour éviter les débordements
        if (!this.isDesktop()) {
            position = "bottom";
        }

        // Appliquer la position
        content.classList.add(`tooltip__content--${position}`);
    }

    handleGlobalClick(e) {
        // Fermer les tooltips si on clique en dehors
        if (
            this.activeTooltip &&
            !this.activeTooltip.element.contains(e.target)
        ) {
            this.hideAllTooltips();
        }
    }

    handleKeydown(e) {
        // Fermer tous les tooltips avec Escape
        if (e.key === "Escape") {
            this.hideAllTooltips();
        }
    }

    observeNewTooltips() {
        // Observer les changements DOM pour les nouveaux tooltips (Livewire)
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Vérifier si le nœud ajouté contient des tooltips
                        const newTooltips = node.querySelectorAll
                            ? node.querySelectorAll(
                                  "[data-tooltip]:not([data-tooltip-initialized])",
                              )
                            : [];

                        newTooltips.forEach((tooltip) => {
                            this.initTooltip(tooltip);
                            tooltip.dataset.tooltipInitialized = "true";
                        });

                        // Vérifier si le nœud lui-même est un tooltip
                        if (
                            node.matches &&
                            node.matches(
                                "[data-tooltip]:not([data-tooltip-initialized])",
                            )
                        ) {
                            this.initTooltip(node);
                            node.dataset.tooltipInitialized = "true";
                        }
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });
    }

    isDesktop() {
        return window.matchMedia("(min-width: 1000px)").matches;
    }
}

// Initialiser le gestionnaire de tooltips quand le DOM est prêt
document.addEventListener("DOMContentLoaded", () => {
    window.tooltipManager = new TooltipManager();
});

// Réinitialiser après les mises à jour Livewire
document.addEventListener("livewire:navigated", () => {
    if (window.tooltipManager) {
        window.tooltipManager.initTooltips();
    }
});

// Export pour utilisation dans d'autres modules si nécessaire
export default TooltipManager;
